import {defineStore} from "pinia";
import {computed, ref} from "vue";
import Reservation from "@/types/clients/esquire/data/general/reservation";
import CrmClient from "@/types/clients/esquire/data/general/crm-client";
import Contract from "@/types/clients/esquire/data/general/contract";
import dayjs from "dayjs";
import {
  getContractStatus,
  getRoutesAndCrm,
  logInRequest,
  reservationLogInRequest
} from "@/services/login-service";
import {RouteLocationNormalized} from "vue-router";
import ReservationLoginPayload from "@/types/clients/esquire/payloads/reservation-login-payload";
import {AuthError} from "@/services/auth-errors";
import GlobalService from "@/services/global-service";
import ReservationLoginData from "@/types/clients/esquire/data/general/reservation-login-data";
import ROLE from "@/types/enums/role";
import {
  fromPersistSnapshot,
  type PersistSnapshot,
  toPersistSnapshot
} from "@/stores/persist/session-persist";
import SessionState from "@/types/enums/session-state";
import LoginData from "@/types/clients/esquire/data/general/login-data";
import {AdminOutcome, PendingPaymentContext} from "@/stores/session/types";
import Language from "@/types/enums/language";

import UpdateGuestPayload from "@/types/clients/esquire/payloads/update-guest-payload";
import PutGuestUpdate from "@/types/clients/esquire/requests/checkin/put-guest-update";
import esquire from "@/types/clients/esquire";
import Guest from "@/types/clients/esquire/data/general/contract-data/guest";
import EsquireService from "@/services/esquire-service";
import {VatNumberStoreResult} from "@/types/clients/esquire/requests/post-vat-number-request";
import {
  GuestContactUpdateResult
} from "@/types/clients/esquire/responses/guest-contact-update-result";
import {useEntryAttributionStore} from "@/stores/session/entry-attribution";


export const useSessionStore = defineStore("sessionStore", () => {
  const _action = ref<Promise<AdminOutcome|void> | null>(null);
  const state = computed(() => _action.value ? SessionState.BUSY : SessionState.IDLE);

  const role = ref<ROLE>(ROLE.ANONYMOUS);

  const reservation = ref<Reservation | null>(null);
  const reservations = ref<Reservation[]>([]);
  const contract = ref<Contract | null>(null);
  const crmClient = ref<CrmClient | null>(null);
  const contractId = ref<string|null>(null);
  const guid = ref<string|null>(null);

  const offerId = ref<string | null>(null);

  // Admin auth artefacts
  const signature = ref<string |null>(null);
  const expires = ref<number|null>(null);
  const crmId = ref<number|null>(null);

  const language = ref<string>("en");

  const lastGuestHydrationAt = ref<number | null>(null);
  const GUEST_REFRESH_TTL_MS = 5 * 60 * 1000; // 5 min

  const hydrationFailed = ref<boolean>(false);


  const units = computed<string[]>(() => {
    const contracts = reservation.value?.contracts ?? [];
    const ids = contracts.map(c => c.unitId).filter(Boolean) as string[];
    return Array.from(new Set(ids));
  });

  // session tracking
  const isExpired = computed(() => {
    const exp = expires.value;
    return Number.isFinite(exp) ? dayjs().isAfter(dayjs.unix(exp as number)) : false;
  });



  // readiness
    const needsGuestHydration = computed(
    (): boolean => !!contractId.value && !reservation.value && !hydrationFailed.value
  );

  const needsAdminHydration = computed(() => {
    const hasAdminParams =
      !!signature.value && !!expires.value && !!crmId.value && !isExpired.value;

    return hasAdminParams && !crmClient.value && !hydrationFailed.value;

  });


  const needsHydration = computed(() => needsGuestHydration.value || needsAdminHydration.value);
  const isBusy = computed(() => state.value === SessionState.BUSY);

  const isLoggedIn = computed(() => {
    if (isExpired.value) return false;
    if (role.value === ROLE.GUEST) return !!reservation.value;
    if (role.value === ROLE.ADMIN) return !!crmClient.value;
    return false;
  });




  // derived
  const isGuest = computed(() => role.value === ROLE.GUEST);
  const isAdmin = computed(() => role.value === ROLE.ADMIN);

  /** Sanitize user-supplied references before sending to API. */
  function sanitizeReference(refStr: string) {
    return (refStr ?? "").trim().toUpperCase();
  }

  /** Ensure only one state-mutating async action runs at a time. */
  function _guardAction() {
    if (isBusy.value) {
      throw new Error("Action already in progress");
    }
  }

  function markGuestHydrated(): void {
    lastGuestHydrationAt.value = Date.now();
  }

  /**
   * Force the app back to the auth view, preserving the current UI language.
   * Clears session state and navigates to the auth route.
   * @param isForced When true, bypasses the busy guard.
   */
  async function reAuth(isForced = true): Promise<void> {
    const lang = language.value ?? "en";
    await GlobalService.router().routeTo("auth");
    logout(isForced); // bypass guard, we're likely inside a running action
    language.value = lang;
  }

  /**
   * Clear session state and mark the user anonymous.
   * @param isForced When true, bypasses the busy guard.
   */
  function logout(isForced = false): void {
    if (!isForced) _guardAction();

    _resetState();
    useEntryAttributionStore().clear();
  }

  /** Internal: reset all session-owned state to a safe baseline. */
  function _resetState(): void {
    const entryAttribution = useEntryAttributionStore();

    role.value = ROLE.ANONYMOUS;
    reservation.value = null;
    reservations.value = [];
    contract.value = null;
    crmClient.value = null;
    signature.value = null;
    expires.value = null;
    crmId.value = null;
    offerId.value = null;
    contractId.value = null;
    guid.value = null;

    entryAttribution.clear();
  }

  /**
   * Capture signed admin params from the route query.
   * Must be called by router guards prior to onReady().
   */
  function setFromRoute(to: RouteLocationNormalized): void {
    const q = to.query;
    if (q.signature) signature.value = String(q.signature);
    if (q.expires)   expires.value = Number(q.expires);
    if (q.crm_id)    crmId.value = Number(q.crm_id);

    const routeLanguage = typeof q.language === "string" ? q.language : null;
    if (routeLanguage && Object.values(Language).includes(routeLanguage as Language)) {
      language.value = routeLanguage;
    }
  }


  // ----- Public API -----


  /**
   * Guest login flow.
   * Serializes with other session mutations via `_action.value`.
   * @throws Error if another action is already in progress.
   */
  async function loginGuest(reference: string): Promise<void> {
    const refSan = sanitizeReference(reference);

    if (_action.value) {
      await _action.value;

      const currentRef = sanitizeReference(contract.value?.id ?? contractId.value ?? "");
      if (reservation.value && currentRef === refSan) return;
    }

    _guardAction();
    return (_action.value = _loginGuest(refSan)).finally(() => (_action.value = null));
  }

  /** Internal: performs the guest login call and applies the payload. */
  async function _loginGuest(reference: string): Promise<void> {
    const refSan = sanitizeReference(reference);
    const entryAttribution = useEntryAttributionStore();
    const pendingAttribution = entryAttribution.peekPendingAttribution();

    try {
      const loginData = await logInRequest.send(refSan, pendingAttribution ?? undefined);
      applyGuestPayload(loginData);

      if (pendingAttribution) {
        entryAttribution.markSent();
      }

    } catch (e) {
      if (e instanceof AuthError) {
        await reAuth();
        return;
      }
      throw e;
    }
  }

  async function refreshGuestSmart(opts?: { background?: boolean }): Promise<void> {
    if (!shouldRefreshGuest()) return;

    if (opts?.background) {
      if (_action.value) return;
      lastGuestHydrationAt.value = Date.now();

      const doWork = (async () => {
        const ref = contract.value?.id ?? contractId.value ?? null;
        if (!ref) return;
        const entryAttribution = useEntryAttributionStore();
        const pendingAttribution = entryAttribution.peekPendingAttribution();

        try {
          const loginData = await logInRequest.send(sanitizeReference(ref), pendingAttribution ?? undefined);
          applyGuestPayload(loginData);

          if (pendingAttribution) {
            entryAttribution.markSent();
          }
        } catch (e) {
          if (e instanceof AuthError) { await reAuth(); return; }
          throw e;
        }
      })();

      _action.value = doWork.then(() => undefined, () => undefined);
      try { await doWork; } finally { _action.value = null; }
      return;
    }

    await refreshGuest();
  }


  /**
   * Refresh the current guest session by reusing the primary contract id.
   * If no reference can be derived, forces re-auth.
   * Serialized against other in-flight actions.
   */
  async function refreshGuest(): Promise<void> {
    if (_action.value) { await _action.value; return; }

    const doWork = (async () => {
      const ref = contract.value?.id ?? contractId.value ?? null;
      if (!ref) return;
      const entryAttribution = useEntryAttributionStore();
      const pendingAttribution = entryAttribution.peekPendingAttribution();

      try {
        const loginData = await logInRequest.send(sanitizeReference(ref), pendingAttribution ?? undefined);
        applyGuestPayload(loginData);

        if (pendingAttribution) {
          entryAttribution.markSent();
        }
      } catch (e) {
        if (e instanceof AuthError) { await reAuth(); return; }
        throw e;
      }
    })();

    _action.value = doWork.then(() => undefined, () => undefined);
    try { await doWork; } finally { _action.value = null; }
  }

  /**
   * Resolve a route URL by key from the active role’s route map.
   * Fail-fast if missing for the current role.
   * @throws Error when the key or routes map is missing.
   */
  function urlFor(key: string): string {
    const map: Record<string, string> | undefined =
      isGuest.value ? reservation.value?.routes : crmClient.value?.routes;

    if (!map || !map[key]) {
      throw new Error(`Missing route "${key}" for role ${role.value}`);
    }
    return map[key];
  }

  const PAYMENT_CTX_TTL_MS = 60 * 60 * 1000; // 1h

  const pendingPayment = ref<PendingPaymentContext | null>(null);

  function setPendingPayment(ctx: Omit<PendingPaymentContext, "createdAt">) {
    pendingPayment.value = { ...ctx, createdAt: Date.now() };
  }

  function consumePendingPayment(): PendingPaymentContext | null {
    const ctx = pendingPayment.value;
    pendingPayment.value = null;
    return ctx;
  }

  const hasPendingPayment = computed(
    () => !!pendingPayment.value && Date.now() - pendingPayment.value.createdAt < PAYMENT_CTX_TTL_MS
  );

  pendingPayment.value = null;



  /**
   * Admin login bootstrap.
   * Uses "contract_id" or "admin" flow based on email presence.
   * Returns an outcome that the caller can use to drive UI.
   */
  async function loginAdmin(reference: string, email: string): Promise<AdminOutcome> {
    _guardAction();
    return (_action.value = _loginAdmin(reference, email)).finally((): void => { _action.value = null; });
  }

  /** Internal: performs the admin login initiation, returns an outcome token. */
  async function _loginAdmin(reference: string, email: string): Promise<AdminOutcome> {
    const refSan: string = sanitizeReference(reference);
    const emailSan: string = (email ?? "").trim();
    const type: "contract_id" | "email" = emailSan ? "email" : "contract_id";

    try {
      // Backend returns "success" or "to_create" (and sends email itself on "success")
      const result = await reservationLogInRequest.send(new ReservationLoginPayload(type, refSan, emailSan));

      if (result === "success") return type === "contract_id" ? "linked" : "sent";
      return "email_required";
    } catch (e) {
      if (e instanceof AuthError) {
        await reAuth();
        return "email_required";
      }
      throw e;
    }
  }

  function shouldRefreshGuest(): boolean {
    if (!isGuest.value) return false;
    if (useEntryAttributionStore().hasPendingAttribution) return true;
    if (!reservation.value) return true;
    if (contract.value?.contractStatus === "expected") return true;
    if (hasPendingPayment.value) return true;
    const last = lastGuestHydrationAt.value ?? 0;
    return Date.now() - last > GUEST_REFRESH_TTL_MS;
  }

  /** Apply the guest login/refresh payload to session state and clear admin artifacts. */
  function applyGuestPayload(loginData: LoginData): void {
    if (!loginData.reservation || !loginData.contractId) {
      throw new AuthError("unauthorized");
    }

    role.value = ROLE.GUEST;
    _clearAdminArtifacts();

    reservation.value = loginData.reservation;
    reservation.value.routes = loginData.routes;

    contractId.value = loginData.contractId;

    contract.value = loginData.reservation.getContractById(loginData.contractId) ?? null;

    if (contract.value) {
      contract.value.contractStatus = loginData.contractStatus;
    }

    markGuestHydrated();
  }

  /** Apply the admin hydration payload to session state and clear guest artifacts. */
  function applyAdminPayload(data: ReservationLoginData): void {
    role.value = ROLE.ADMIN;
    _clearGuestArtifacts();

    reservations.value = data.reservations ?? [];
    const clientData = data.crmClient;
    clientData.routes = data.routes;
    clientData.amountOfOffers = data.amountOfOffers;
    crmClient.value = clientData;
  }

  async function updateGuestContact(resId: string, guest: Guest): Promise<GuestContactUpdateResult> {
    _guardAction();

    const trim = (s?: string): string => (s ?? "").trim();

    const doWork = (async (): Promise<GuestContactUpdateResult> => {
      guest.firstName = trim(guest.firstName);
      guest.lastName = trim(guest.lastName);
      guest.email = trim(guest.email);
      guest.phone = trim(guest.phone);
      guest.companyName = trim(guest.companyName);
      guest.companyVat = trim(guest.companyVat);
      guest.address = trim(guest.address);
      guest.city = trim(guest.city);
      guest.zipcode = trim(guest.zipcode);

      const payload = new UpdateGuestPayload(guest, resId);
      const request = new PutGuestUpdate(esquire);
      const result = await request.send(payload);

      if (result.data.guest_updated && reservation.value?.contracts?.length) {
        const targetId = contractId.value ?? contract.value?.id ?? null;
        const idx = reservation.value.contracts.findIndex((c) => c.id === targetId);

        if (idx >= 0) {
          reservation.value.contracts[idx].guest = guest;
        }
      }

      return result;
    })();

    _action.value = doWork.then((): void => undefined, (): void => undefined);

    try {
      return await doWork;
    } finally {
      _action.value = null;
    }
  }


  /**
   * Refresh the admin session (routes + CRM client) using signed params.
   * Forces re-auth if the signature has expired.
   * Serialized against other in-flight actions.
   */
  async function refreshAdmin(): Promise<void> {
    if (_action.value) {
      await _action.value;
      return;
    }

    if (!signature.value || !expires.value || !crmId.value) return;
    if (isExpired.value) {
      await reAuth();
      return;
    }

    const sig = signature.value as string;
    const exp = expires.value as number;
    const id = crmId.value as number;
    const entryAttribution = useEntryAttributionStore();
    const pendingAttribution = entryAttribution.peekPendingAttribution();

    const doWork = (async (): Promise<void> => {
      const data = await getRoutesAndCrm.send(sig, exp, id, pendingAttribution ?? undefined);
      applyAdminPayload(data);

      if (pendingAttribution) {
        entryAttribution.markSent();
      }
    })();

    _action.value = doWork.then((): void => undefined, (): void => undefined);

    try {
      await doWork;
    } finally {
      _action.value = null;
    }
  }


  /**
   * Ensure session is hydrated if signed admin params are present (router guard path).
   * No-op if another action is in-flight. Re-auths on invalid/expired signature.
   */
  async function onReady(): Promise<void> {
    if (_action.value || hydrationFailed.value) return;

    try {
      if (needsAdminHydration.value) {
        await refreshAdmin();
      }

      if (needsGuestHydration.value) {
        await refreshGuest();
      }
    } catch (e) {
      failHydration();
    }
  }


  /**
   * If the guest's contract status is "expected", refetch it.
   * If it turns "active", re-login the guest to hydrate reservation/routes.
   */
  async function ensureContractStatusFresh(): Promise<void> {
    if (!isGuest.value || !contract.value) return;
    if (contract.value?.contractStatus !== "expected") return;

    if (_action.value) await _action.value;

    const doWork = (async () => {
      try {
        const newStatus = await getContractStatus.send();
        if (!contract.value) return;
        contract.value.contractStatus = newStatus;

        if (newStatus === "active") {
          await refreshGuest();
        }
      } catch (e) {
        if (e instanceof AuthError) { await reAuth(); return; }
        throw e;
      }
    })();

    _action.value = doWork.then(() => undefined, () => undefined);
    try { await doWork; } finally { _action.value = null; }
  }

  /** Clear all admin-related state. */
  function _clearAdminArtifacts(): void {
    reservations.value = [];
    crmClient.value = null;
    signature.value = null;
    expires.value = null;
    crmId.value = null;
  }

  /** Clear all guest-related state. */
  function _clearGuestArtifacts(): void {
    reservation.value = null;
    contract.value = null;
    offerId.value = null;
  }

  /**
   * Normalizes a VAT company name returned from external validation (e.g. VIES).
   *
   * Some providers (Spanish & German countries) return placeholder values like "---" or empty strings when
   * no company name is available. This helper ensures we do not persist or use
   * meaningless data in the application.
   *
   * Behavior:
   * - Trims the input value
   * - Returns null if the value is empty or equals "---"
   * - Returns the cleaned string otherwise
   *
   * @param companyName Raw company name from VAT validation response
   * @returns Cleaned company name or null if invalid/unusable
   */
  function normalizeVatCompanyName(companyName?: string | null): string | null {
    const value = companyName?.trim() ?? "";
    return value === "" || value === "---" ? null : value;
  }

  function failHydration(): void {
    const lang = language.value ?? "en";

    hydrationFailed.value = true;
    _resetState();

    language.value = lang;
  }

  async function validateVatNumber(contractIdParam: string, vatNumber: string): Promise<VatNumberStoreResult> {
    _guardAction();

    const doWork = (async (): Promise<VatNumberStoreResult> => {
      const res = await EsquireService.vat().validate(contractIdParam, vatNumber);
      const normalizedCompanyName = normalizeVatCompanyName(res.vat?.companyName);

      if (res.vat?.isValid && contract.value?.guest && reservation.value?.contact) {
        const g = contract.value.guest;
        const v = reservation.value.contact;

        g.companyName = normalizedCompanyName ?? g.companyName ?? "";
        g.companyVat = res.vat.number ?? g.companyVat ?? "";
        v.companyName = normalizedCompanyName ?? v.companyName ?? g.companyName ?? "";
        v.companyVatCode = res.vat.number ?? g.companyVat ?? "";
        g.address = res.vat.address ?? g.address ?? "";
        g.zipcode = res.vat.postalCode ?? g.zipcode ?? "";
        g.city = res.vat.city ?? g.city ?? "";
      }

      return {
        ...res,
        vat: res.vat
          ? {
            ...res.vat,
            companyName: normalizedCompanyName
          }
          : res.vat
      };
    })();

    _action.value = doWork.then((): undefined => undefined, (): undefined => undefined);

    try {
      return await doWork;
    } finally {
      _action.value = null;
    }
  }


  /** Convenience: find the contract id for a given unit id on the active reservation. */
  function contractIdByUnitId(unitId: string): string | null {
    return reservation.value?.contracts.find(c => c.unitId === unitId)?.id ?? null;
  }

  /**
   * Admin-only CRM client (non-nullable).
   *
   * Throws an Error if accessed outside an admin session or if the CRM client
   * hasn’t been hydrated yet. Use this only in admin-only code paths/components.
   * It is the caller’s responsibility to ensure the session is admin before use.
   */
  const adminCrmClient = computed((): CrmClient => {
    if (!isAdmin.value) throw new Error("CRM client not available outside admin sessions");
    const cc = crmClient.value;
    if (!cc) throw new Error("CRM client missing");
    return cc as CrmClient;
  });

  /**
   * Guest-only Reservation (non-nullable).
   *
   * Throws an Error if accessed outside a guest session or if the reservation
   * hasn’t been hydrated yet. Use this only in guest-only code paths/components.
   * It is the caller’s responsibility to ensure the session is guest before use.
   */
  const guestReservation = computed((): Reservation => {
    if (!isGuest.value) throw new Error("Reservation not available outside guest sessions");
    const r = reservation.value;
    if (!r) throw new Error("Reservation missing");
    return r as Reservation;
  });

  const guestPrimaryContract = computed((): Contract => {
    if (!isGuest.value || !contract.value) throw new Error("Primary contract not available");
    return contract.value as Contract;
  });

  return {
    // state
    role, reservation, reservations, contract, crmClient, signature, expires, crmId, language, contractId, guid,
    offerId, adminCrmClient, guestReservation, guestPrimaryContract,needsHydration, isBusy,
    pendingPayment, hasPendingPayment,
    // getters
    isGuest, isAdmin, isLoggedIn, isExpired, units,
    // actions
    setFromRoute, loginGuest, loginAdmin, onReady, logout, reAuth, urlFor, shouldRefreshGuest,
    refreshAdmin,refreshGuestSmart, refreshGuest, contractIdByUnitId, ensureContractStatusFresh,
    setPendingPayment, consumePendingPayment, updateGuestContact, validateVatNumber,
    hydrationFailed, failHydration
  };
}, {
  persist: {
    key: "session",
    storage: localStorage,
    serializer: {
      serialize: (raw: unknown): string => JSON.stringify(toPersistSnapshot(raw as any)),
      deserialize: (value: string): any => fromPersistSnapshot(JSON.parse(value) as PersistSnapshot)
    }
  }
});
